/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/**
 * This is the entry point for Feature Hub App integration
 */
import * as React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import { QueryCache } from 'react-query';
import I18nContextComponent from '@oneaudi/i18n-context';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import { type ApolloClient, ApolloProvider, type NormalizedCacheObject } from '@apollo/client';
import type {
  AsyncSsrManager,
  ContentService,
  EnvConfigService,
  GfaLocaleServiceV1,
  Logger,
  SerializedStateManager,
  TrackingService,
  QueryCacheService,
  I18NService,
  RenderModeServiceV1,
} from './feature-service-types';
import { FeatureAppProvider } from './FeatureAppProvider';
import type { ConfigProps } from './components/PartnerBusinessCard';
import FeatureApp from './FeatureApp';
import { i18nScope } from '../i18n/index';
import { getDealerQueryVariables, queryString } from '../queryHook';
import { JsonLd } from './components/Json-ld';
import type { PbcDataPartner } from './components/PartnerData';
import {
  type EditorJsonContent,
  type FalconContent,
  mapContent,
} from './components/headless/ContentInterfaces';
import { createApolloClient } from '../lib/client';
import { getDesignLibrary } from './alpha-helper';
import FeatureServiceContext from './FeatureServiceContext';

export const name = 'fa-partner-business-card';

interface RequiredFeatureServiceDependencies extends FeatureServices {
  readonly 'audi:envConfigService'?: EnvConfigService;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
  readonly 'dbad:audi-i18n-service'?: I18NService;
}

interface OptionalFeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'audi-tracking-service'?: TrackingService;
  readonly 's2:async-ssr-manager'?: AsyncSsrManager;
  readonly 's2:serialized-state-manager'?: SerializedStateManager;
  readonly 'audi-ddmc:query-cache-service'?: QueryCacheService;
  readonly 'audi-content-service'?: ContentService;
  readonly 'audi-render-mode-service'?: RenderModeServiceV1;
}

export interface FeatureServiceDependencies
  extends RequiredFeatureServiceDependencies, OptionalFeatureServiceDependencies, FeatureServices {}

export interface Env {
  omnigraphEndpointUrl: string;
}

export interface FeatureAppAsyncState {
  env: Env;
}

export async function fetchEnv(envConfigService: EnvConfigService) {
  const configName = 'partner-business-card';
  const env = await envConfigService.getConfiguration<Env>(configName);

  if (!env) {
    throw new Error(`Error while fetching ${configName} environment configuration`);
  }

  return env;
}

export type FeatureAppAsyncStateHolder =
  | (() => Promise<FeatureAppAsyncState>)
  | FeatureAppAsyncState
  | undefined;

type JsonLdState = {
  content: EditorJsonContent;
  data: PbcDataPartner;
};

const computeJsonLdConfig = (
  editorContent: EditorJsonContent,
): { jsonLdType: string; socialMedia: any; shouldShowJsonLd: boolean } => {
  let jsonLdType: string;
  let socialMedia: any;
  let shouldShowJsonLd: boolean;

  if (editorContent?.variantConfig) {
    jsonLdType = editorContent?.variantConfig?.contactDetails?.showJsonLdAutoDealer
      ? 'AutoDealer'
      : 'LocalBusiness';
    socialMedia = editorContent?.variantConfig?.socialMedia;
    shouldShowJsonLd =
      editorContent?.variantConfig?.contactDetails?.showJsonLd ||
      editorContent?.variantConfig?.contactDetails?.showJsonLdAutoDealer ||
      false;
  } else {
    jsonLdType = editorContent?.variantConfigHeadless?.showJsonLdAutoDealer
      ? 'AutoDealer'
      : 'LocalBusiness';
    socialMedia = editorContent?.variantConfigHeadless?.socialMedia;
    shouldShowJsonLd =
      editorContent?.variantConfigHeadless?.showJsonLd ||
      editorContent?.variantConfigHeadless?.showJsonLdAutoDealer ||
      false;
  }

  return { jsonLdType, socialMedia, shouldShowJsonLd };
};

export const getAsyncStateHolder = async (
  contentService: ContentService,
  config: any,
  client: ApolloClient<NormalizedCacheObject>,
  localeService: GfaLocaleServiceV1,
): Promise<JsonLdState> => {
  const mappedContent = mapContent(
    (await contentService?.getContent()) as EditorJsonContent | FalconContent,
  );
  const id = (mappedContent.partnerID || config?.partnerID) as string | undefined;

  const { data: queryData } = await client.query({
    query: queryString,
    variables: getDealerQueryVariables(id, localeService.language),
  });

  const partnerData: PbcDataPartner = queryData?.dealerById;

  return { content: mappedContent, data: partnerData };
};

type JsonLdContainerProps = {
  initialState: JsonLdState | null;
  contentService?: ContentService;
  config: any;
  client: ApolloClient<NormalizedCacheObject>;
  localeService: GfaLocaleServiceV1;
};

const JsonLdContainer: React.FC<JsonLdContainerProps> = ({
  initialState,
  contentService,
  config,
  client,
  localeService,
}) => {
  const logger = React.useContext(FeatureServiceContext).logger || console;
  const [state, setState] = React.useState(initialState);

  React.useEffect(() => {
    if (state || !contentService) {
      return;
    }

    let cancelled = false;

    getAsyncStateHolder(contentService, config, client, localeService)
      .then((result) => {
        if (cancelled) return;
        setState(result);
      })
      .catch((err: unknown) => {
        logger.error(err);
      });

    return () => {
      cancelled = true;
    };
  }, [state, contentService, config, client, logger, localeService]);

  if (!state) return null;

  const { content, data } = state;
  const { jsonLdType, socialMedia, shouldShowJsonLd } = computeJsonLdConfig(content);

  if (!shouldShowJsonLd) return null;

  return (
    <JsonLd
      jsonLdType={jsonLdType}
      data={data}
      socialMedia={socialMedia}
      localeService={localeService}
    />
  );
};

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    externals: {
      react: '^16.13.0||^17.0.2||^18.x.x',
      'styled-components': '^5.1.1',
      '@feature-hub/react': '^3.x.x',
      ...getDesignLibrary({ unifiedWebCommonVersion: '*', unifiedWebAlphaCommonVersion: '*' }),
    },
    featureServices: {
      'audi:envConfigService': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
    },
  },

  optionalDependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'audi-tracking-service': '^2.1.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-ddmc:query-cache-service': '^1.0.0',
      'audi-content-service': '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
    },
  },

  create: ({
    featureServices,
    config,
  }: FeatureAppEnvironment<FeatureServiceDependencies, ConfigProps>) => {
    const localeService = featureServices['gfa:locale-service'];

    const logger = featureServices['s2:logger'] || console;
    const i18nData = featureServices['dbad:audi-i18n-service'] as I18NService;
    const trackingService = featureServices['audi-tracking-service'];

    const contentService = featureServices['audi-content-service'];

    if (typeof window !== 'undefined' && trackingService) {
      trackingService.featureAppName = name;
    }

    const asyncSSRManager = featureServices['s2:async-ssr-manager'];
    const serializedStateManager = featureServices['s2:serialized-state-manager'];

    const client = createApolloClient(localeService);

    let initialJsonLdState: JsonLdState | null = null;

    if (asyncSSRManager && contentService) {
      const waitForThese = async () => {
        const holder = await getAsyncStateHolder(contentService, config, client, localeService);
        initialJsonLdState = holder;
        serializedStateManager?.register(() => JSON.stringify(holder));
      };
      const promiseWaitForThese = waitForThese();
      asyncSSRManager.scheduleRerender(promiseWaitForThese);
    } else if (serializedStateManager) {
      const serializedState = serializedStateManager.getSerializedState();
      if (serializedState) {
        try {
          initialJsonLdState = JSON.parse(serializedState) as JsonLdState;
        } catch (e) {
          console.error('Failed to parse serialized state for partner-business-card', e);
          initialJsonLdState = null;
        }
      }
    }

    const queryCacheService = featureServices['audi-ddmc:query-cache-service'] || {
      queryCache: new QueryCache(),
    };

    return {
      render() {
        return (
          <UniversalEditorProvider
            contentService={featureServices['audi-content-service']!}
            renderModeService={featureServices['audi-render-mode-service']}
          >
            <FeatureAppProvider
              localeService={localeService}
              logger={logger}
              trackingService={trackingService}
              contentService={contentService}
              queryCacheService={queryCacheService}
            >
              <ApolloProvider client={client}>
                <I18nContextComponent
                  i18nData={i18nData}
                  language={localeService.language}
                  scopes={[i18nScope]}
                >
                  <JsonLdContainer
                    initialState={initialJsonLdState}
                    contentService={contentService}
                    config={config}
                    client={client}
                    localeService={localeService}
                  />
                  <FeatureApp
                    config={config}
                    initialContent={initialJsonLdState?.content}
                    initialDealerData={initialJsonLdState?.data}
                  />
                </I18nContextComponent>
              </ApolloProvider>
            </FeatureAppProvider>
          </UniversalEditorProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
